import styled, { css } from 'styled-components';
import theme from '../../styles/theme';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'secondary-1' | 'tertiary' | 'tertiary-filled' | 'contained' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  iconAlign?: 'center' | 'left' | 'right' | 'none';
  fullwidth?: boolean;
  disabled?: boolean;
  icon: string;
  status?: 'normal' | 'error';
}

export const Button = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border: none;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  outline: none;

  &::-moz-focus-inner {
    border: 0;
  }

  ${({ size }) => getSize(size)}

  ${({ size, iconAlign }) => getPadding(size, iconAlign)}

  ${({ fullwidth }) => fullwidth && getFullWidth()}
    
  ${({ disabled }) => disabled && getDisabled()}
    
  ${(props) => getVariant(props)}
`;

//@ts-ignore
const getVariant = (properties: ButtonProps) => {
  switch (properties.variant) {
    case 'primary':
      return css`
        background-color: ${theme.colors['primary'].bg.regular};
        color: ${theme.colors['neutral'].text.inverted};

        &:hover:not(:disabled) {
          background-color: ${theme.colors['primary'].bg.strong};
        }

        &:active:not(:disabled) {
          background-color: ${theme.colors['secondary-1'].bg.regular};
        }

        &:disabled {
          background-color: ${theme.colors['success'].bg.weak};
          color: ${theme.colors['neutral'].text.muted};
        }
      `;

    case 'secondary':
      return css`
        background-color: transparent;
        box-shadow: inset 0 0 0 1px ${theme.colors['primary'].border.regular};
        color: ${theme.colors['neutral'].text.regular};
        font-weight: bold;

        &:active:not(:disabled) {
          box-shadow: inset 0 0 0 2px ${theme.colors['primary'].bg.regular};
        }

        &:disabled {
          background-color: transparent;
          box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weaker};
          color: ${theme.colors['neutral'].text.muted};
        }
      `;
    case 'secondary-1':
      return css`
        background-color: transparent;
        box-shadow: inset 0 0 0 1px ${theme.colors['secondary-1'].border.regular};
        color: ${theme.colors['neutral'].text.regular};
        font-weight: bold;

        &:active {
          box-shadow: inset 0 0 0 2px ${theme.colors['secondary-1'].bg.regular};
        }

        ${(props) =>
          properties.disabled &&
          css`
            box-shadow: inset 0 0 0 2px ${theme.colors['neutral'].border.strong};
            color: ${theme.colors['neutral'].text.regular};
          `}
      `;
    case 'tertiary':
      return css`
        padding: ${theme.spacing['spacing-3']};
        background-color: transparent;
        color: ${theme.colors['info'].text.regular};
        font-weight: bold;

        &:hover {
          background-color: ${theme.colors['info'].bg.inverted};
        }

        &:active {
          background-color: ${theme.colors['primary'].bg.regular};
        }

        ${(props) =>
          properties.disabled &&
          css`
            color: ${theme.colors['neutral'].text.weak};
          `}
      `;
    case 'tertiary-filled':
      return css`
        padding: ${theme.spacing['spacing-3']};
        background-color: ${theme.colors['primary'].bg.regular};
        color: ${theme.colors['neutral'].text.inverted};
        font-weight: bold;
        border-radius: ${theme.borderRadius['border-radius-3']};
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: ${theme.colors['primary'].bg.strong};
        }

        &:active {
          background-color: ${theme.colors['secondary-1'].bg.regular};
        }

        ${(props) =>
          properties.disabled &&
          css`
            background-color: ${theme.colors['neutral'].bg.stronger};
            color: ${theme.colors['neutral'].text.weak};
          `}
      `;
    case 'contained':
      return css`
        background-color: ${theme.colors['neutral'].bg.regular};
        box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.regular};
        color: ${theme.colors['primary'].text.regular};
        justify-content: space-between;
        text-align: left;
        i {
          position: absolute;
          right: 16px;
        }
        ${(props) =>
          properties.disabled &&
          css`
            box-shadow: inset 0 0 0 2px ${theme.colors['neutral'].border.strong};
            color: ${theme.colors['neutral'].text.regular};
          `}

        ${(props) =>
          properties.status === 'error' &&
          css`
            box-shadow: inset 0 0 0 1px ${theme.colors.danger.border.regular};
            stroke: ${theme.colors.danger.border.regular};
          `}
      `;
    case 'danger':
      return css`
        background-color: ${theme.colors['danger'].bg.regular};
        box-shadow: inset 0 0 0 1px ${theme.colors['danger'].border.regular};
        color: ${theme.colors['neutral'].text.inverted};
        justify-content: space-between;
        ${(props) =>
          properties.disabled &&
          css`
            box-shadow: inset 0 0 0 2px ${theme.colors['danger'].border.strong};
            color: ${theme.colors['neutral'].text.regular};
          `}
      `;
    case 'success':
      return css`
        background-color: ${theme.colors['success'].bg.stronger};
        color: ${theme.colors['neutral'].text.inverted};

        &:hover {
          background-color: ${theme.colors['success'].bg.strong};
        }

        &:active {
          background-color: ${theme.colors['success'].bg.strong};
        }

        ${(props) =>
          properties.disabled &&
          css`
            background-color: ${theme.colors['neutral'].bg.stronger};
            color: ${theme.colors['neutral'].text.regular};
          `}
      `;
  }
};

const getSize = (size) => {
  switch (size) {
    case 'sm':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-9']};
      `;
    case 'md':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-9']};
      `;
    case 'lg':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-7']};
      `;
    case 'xl':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-7']};
      `;
    case '2xl':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-4']};
      `;
    case 'md-full':
      return css`
        border-radius: ${theme.borderRadius['border-radius-3']};
        ${theme.textStyles['text-preset-6']};
        ${getFullWidth()}
      `;
  }
};

const getPadding = (size = 'md', iconAlign = 'none') => {
  const paddingsBySize = {
    sm: {
      none: `
        ${theme.spacing['spacing-0']}
        ${theme.spacing['spacing-2']}`,
      left: `
        ${theme.spacing['spacing-0']}
        ${theme.spacing['spacing-2']}`,
      right: `
        ${theme.spacing['spacing-0']}
        ${theme.spacing['spacing-2']}`,
      center: `${theme.spacing['spacing-0']}`,
    },
    md: {
      none: `
        ${theme.spacing['spacing-1']}
        ${theme.spacing['spacing-3']}`,
      left: `
        ${theme.spacing['spacing-1']}
        ${theme.spacing['spacing-3']}`,
      right: `
        ${theme.spacing['spacing-1']}
        ${theme.spacing['spacing-3']}`,
      center: `${theme.spacing['spacing-1']}`,
    },
    lg: {
      none: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      left: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      right: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      center: `${theme.spacing['spacing-3']}`,
    },
    xl: {
      none: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      left: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      right: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      center: `${theme.spacing['spacing-3']}`,
    },
    '2xl': {
      none: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      left: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      right: `
          ${theme.spacing['spacing-3']}
          ${theme.spacing['spacing-4']}`,
      center: `${theme.spacing['spacing-3']}`,
    },
    'md-full': {
      none: `${theme.spacing['spacing-4']}`,
      left: `${theme.spacing['spacing-4']}`,
      right: `${theme.spacing['spacing-4']}`,
      center: `${theme.spacing['spacing-4']}`,
    },
  };

  const sizeConfig = paddingsBySize[size] || paddingsBySize['md'];
  const padding = sizeConfig[iconAlign] || sizeConfig['none'];

  return css`
    padding: ${padding};
    ${iconAlign === 'center' &&
    size !== 'md-full' &&
    `
          display: flex;
          justify-content: center;
          align-items: center; 
        `}

    i {
      margin-right: ${iconAlign === 'left' ? theme.spacing['spacing-3'] : 0};
      margin-left: ${iconAlign === 'right' ? theme.spacing['spacing-3'] : 0};
    }
  `;
};

const getFullWidth = () => css`
  width: 100%;
  justify-content: center;
`;

const getDisabled = () => css`
  cursor: not-allowed;
  pointer-events: none;
`;
